import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useProjectsApi } from "hooks/useProjects";
import { getProjectId } from "types/project.types";
import { selectSelectedDocumentIdForProject } from "store/reducers/project";
import { AppState } from "store/reducers/Reducer";
import { resolveScreenplayDocumentId } from "utils/project/resolveScreenplayDocumentId";
import { hydrateProjectFromDetailCache } from "utils/project/hydrateProjectFromDetailCache";

type ProjectWithDocumentLists = {
  versions?: Array<{ documentId?: string; document_id?: string }>;
  documents?: Array<{ documentId?: string; document_id?: string }>;
};

/**
 * Custom hook to get project identifiers and data from URL params
 * Provides project, projectId, documentId, and validation state
 *
 * By being on a page that uses this hook, we are guaranteed that projectId and documentId exist.
 * The route requires :projectId, and the project must have a documentId to be accessible.
 * Therefore, projectId and documentId are always string | number (never undefined).
 * This supports both numeric IDs and UUIDs (for future migration).
 *
 * @param enablePolling - Whether to enable automatic polling for project data
 * @param documentIdForFeatureStatuses - When set, load feature statuses scoped to this document (version)
 * @returns Object containing project data, IDs (always string | number), loading state, and validation
 *
 * @example
 * ```tsx
 * const { projectId, documentId, hasValidIds } = useProjectIdentifiers();
 * // projectId and documentId are always string | number, never undefined
 * if (!hasValidIds) return <Loader />; // Only check for loading state
 * ```
 */
export const useProjectIdentifiers = (
  enablePolling?: boolean,
  documentIdForFeatureStatuses?: string | number
) => {
  const { projectId: paramProjectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  // The store owns the pick with its project, so another project's pick never applies here.
  const selectedDocumentId = useSelector((state: AppState) =>
    selectSelectedDocumentIdForProject(state.project, paramProjectId)
  );
  const api = useProjectsApi({ take: 10, order: "DESC" });
  const useProject = api.useProject;

  const documentScopeForStatuses = useMemo(() => {
    if (
      documentIdForFeatureStatuses != null &&
      String(documentIdForFeatureStatuses) !== "" &&
      String(documentIdForFeatureStatuses) !== "0"
    ) {
      return String(documentIdForFeatureStatuses);
    }
    return undefined;
  }, [documentIdForFeatureStatuses]);

  const validId = useMemo(
    () => Boolean(paramProjectId && paramProjectId !== "0"),
    [paramProjectId]
  );

  const {
    data: project,
    isLoading,
    isError,
    error,
    refetch: refetchProject,
  } = useProject(validId ? paramProjectId : undefined, enablePolling, documentScopeForStatuses);

  const result = useMemo(() => {
    const routeProjectId = (paramProjectId ?? "") as string | number;
    const hydratedProject = hydrateProjectFromDetailCache(
      queryClient,
      String(routeProjectId),
      project
    );
    const effectiveProject = hydratedProject ?? project;

    if (!effectiveProject) {
      const documentId = resolveScreenplayDocumentId({
        queryClient,
        projectId: String(routeProjectId),
        preferredDocumentId: selectedDocumentId,
      });
      const hasValidIds = Boolean(
        routeProjectId && documentId && documentId !== "" && documentId !== "0"
      );

      return {
        project: null,
        projectId: routeProjectId,
        documentId: (documentId || "") as string | number,
        hasValidIds,
        isLoading: Boolean(isLoading),
        isProjectError: Boolean(isError),
        projectError: (error as Error | null) ?? null,
        refetchProject,
      };
    }

    const projectId = getProjectId(effectiveProject) || paramProjectId || "";
    const validSelectedDocumentId = isSelectedDocumentValid(effectiveProject, selectedDocumentId)
      ? selectedDocumentId
      : null;
    const preferredDocumentId = validSelectedDocumentId ?? undefined;
    const documentId = resolveScreenplayDocumentId({
      queryClient,
      projectId: String(projectId),
      preferredDocumentId,
      project: effectiveProject,
    });
    const hasValidIds = Boolean(projectId && documentId && documentId !== "" && documentId !== "0");

    return {
      project: effectiveProject,
      projectId: (projectId ?? "") as string | number,
      documentId: (documentId ?? "") as string | number,
      hasValidIds,
      isLoading: Boolean(isLoading),
      isProjectError: Boolean(isError),
      projectError: (error as Error | null) ?? null,
      refetchProject,
    };
  }, [
    project,
    isLoading,
    isError,
    error,
    refetchProject,
    selectedDocumentId,
    paramProjectId,
    queryClient,
  ]);

  return result;
};

function isSelectedDocumentValid(
  project: ProjectWithDocumentLists,
  selectedDocumentId: string | null
) {
  if (!selectedDocumentId) return false;

  const documentIds = [
    ...(project.versions ?? []).map((version: { documentId?: string; document_id?: string }) =>
      String(version.documentId ?? version.document_id ?? "")
    ),
    ...(project.documents ?? []).map((document: { documentId?: string; document_id?: string }) =>
      String(document.documentId ?? document.document_id ?? "")
    ),
  ].filter(Boolean);

  return documentIds.includes(String(selectedDocumentId));
}
